import { Link, useLocation } from "react-router";
import classNames from "classnames";
import { INavigation } from "../../interfaces/navigation.interface";
import { useState } from "react";
import { ChevronRightIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { ExportSquare } from "iconsax-react";

type Props = {
    path: string;
    name: string;
    icon: any;
    iconActive?: any;
    subItems: INavigation[];
    isExpanded: boolean;
    isCurrent?: boolean;
    isExternal?: boolean;
    disabled?: boolean;
};

const SideBarItem: React.FC<Props> = ({
    path,
    name,
    subItems,
    isExpanded,
    isCurrent,
    isExternal,
    disabled,
    ...props
}) => {
    const location = useLocation();
    const hasActiveSubItem = subItems?.some((item) => location?.pathname === item?.path);
    const [isSubmenuOpen, setIsSubmenuOpen] = useState(hasActiveSubItem ?? false);

    if (disabled) {
        return (
            <li data-testid={`sidebar-item-${name.toLowerCase()}`}>
                <div
                    className="flex items-center gap-3 px-3 py-2 rounded-lg opacity-50 cursor-not-allowed select-none w-full"
                    title="Available once your API keys are configured"
                >
                    {props.icon && <props.icon className="w-5 h-5 flex-shrink-0 text-white" aria-hidden="true" />}
                    <span className={`text-sm text-white truncate min-w-0 ${isExpanded ? "block" : "hidden lg:block"}`}>{name}</span>
                </div>
            </li>
        );
    }

    const rootClass = classNames(
        "menu-horizontal w-full py-3 my-1 text-sm text-white",
        {
            "bg-primary text-white font-bold rounded-lg": isCurrent,
            "pl-4": isExpanded,
            "justify-center !p-3 lg:!pl-4 lg:justify-start": !isExpanded,
        }
    );
    const iconClass = classNames("flex-shrink-0 w-5 h-5 text-white", {
        "text-white": isCurrent,
        "mr-3": isExpanded,
        "lg:mr-3": !isExpanded,
    });

    const iconClassExternal = classNames("lg:ml-auto lg:mr-2 w-4 h-4");

    return (
        <li data-testid={`sidebar-item-${name.toLowerCase()}`}>
            {subItems ? (
                <div className="flex flex-col p-0">
                    <Link
                        to="#"
                        data-testid={`sidebar-item-link-${name.toLowerCase()}`}
                        className={`${rootClass} ${subItems ? `hover:bg-transparent flex ${isExpanded ? 'flex-row' : 'items-center justify-center flex-col'} lg:flex-row lg:justify-start` : ""
                            }`}
                        onClick={() => setIsSubmenuOpen(!isSubmenuOpen)}
                    >
                        <props.icon className={iconClass} aria-hidden="true" data-testid={`sidebar-item-icon-${name.toLowerCase()}`} />
                        <span data-testid={`sidebar-item-text-${name.toLowerCase()}`} className={`truncate min-w-0 ${isExpanded ? "block" : "hidden lg:block"}`}>{name}</span>
                        {isSubmenuOpen ? (
                            <ChevronDownIcon
                                className={`md:ml-auto ${isExpanded ? 'ml-auto mr-2' : ''} lg:mr-2 w-4 h-4`}
                                aria-hidden="true"
                                data-testid={`sidebar-item-chevron-down-${name.toLowerCase()}`}
                            />
                        ) : (
                            <>
                                <ChevronRightIcon
                                    className="hidden lg:block lg:ml-auto lg:mr-2 h-4 w-4"
                                    aria-hidden="true"
                                    data-testid={`sidebar-item-chevron-right-${name.toLowerCase()}`}
                                />

                                <ChevronDownIcon
                                    className={`block lg:hidden ${isExpanded ? 'ml-auto mr-2' : ''} lg:ml-auto lg:mr-2 w-4 h-4`}
                                    aria-hidden="true"
                                    data-testid={`sidebar-item-chevron-down-mobile-${name.toLowerCase()}`}
                                />
                            </>
                        )}
                    </Link>
                    <ul
                        data-testid={`sidebar-item-submenu-${name.toLowerCase()}`}
                        className={
                            isSubmenuOpen ? `block w-full ${isExpanded ? 'pl-5' : ''} lg:pl-5` : "hidden"
                        }
                    >
                        {subItems.map((item, idx) => (
                            <SideBarItem
                                icon={item.icon as any}
                                name={item?.name}
                                iconActive={item.iconActive}
                                path={item?.path}
                                subItems={item.subItems}
                                key={idx}
                                isExpanded={isExpanded}
                                isCurrent={location?.pathname === item?.path}
                                disabled={disabled || item.disabled}
                            />
                        ))}
                    </ul>
                </div>
            ) : (
                <>
                    {
                        isExternal ? (
                            <a href={path} className={rootClass} data-testid={`sidebar-item-link-${name.toLowerCase()}`} target="_blank" rel="noopener">
                                {isCurrent ? (
                                    props.iconActive ? (
                                        <props.iconActive
                                            className={iconClass}
                                            aria-hidden="true"
                                            data-testid={`sidebar-item-icon-active-${name.toLowerCase()}`}
                                        />
                                    ) : (
                                        <props.icon
                                            className={iconClass}
                                            aria-hidden="true"
                                            data-testid={`sidebar-item-icon-${name.toLowerCase()}`}
                                        />
                                    )
                                ) : (
                                    <props.icon
                                        className={iconClass}
                                        aria-hidden="true"
                                        data-testid={`sidebar-item-icon-${name.toLowerCase()}`}
                                    />
                                )}
                                <span data-testid={`sidebar-item-text-${name.toLowerCase()}`} className={`truncate min-w-0 ${isExpanded ? "block" : "hidden lg:block"}`}>{name}</span>
                                <ExportSquare className={iconClassExternal} />
                            </a>
                        ) : (
                            <Link to={path} className={rootClass} data-testid={`sidebar-item-link-${name.toLowerCase()}`}>
                                {isCurrent ? (
                                    props.iconActive ? (
                                        <props.iconActive
                                            className={iconClass}
                                            aria-hidden="true"
                                            data-testid={`sidebar-item-icon-active-${name.toLowerCase()}`}
                                        />
                                    ) : (
                                        <props.icon
                                            className={iconClass}
                                            aria-hidden="true"
                                            data-testid={`sidebar-item-icon-${name.toLowerCase()}`}
                                        />
                                    )
                                ) : (
                                    <props.icon
                                        className={iconClass}
                                        aria-hidden="true"
                                        data-testid={`sidebar-item-icon-${name.toLowerCase()}`}
                                    />
                                )}
                                <span data-testid={`sidebar-item-text-${name.toLowerCase()}`} className={`truncate min-w-0 ${isExpanded ? "block" : "hidden lg:block"}`}>{name}</span>
                            </Link>
                        )
                    }

                </>
            )}
        </li>
    );
};

export default SideBarItem;
